import { shallowEqual } from 'react-redux';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';
import type {
    QueryArgFrom,
    SkipToken,
    UseQueryStateResult,
    MutationResultSelectorResult,
    UseMutationStateResult,
} from '../rtk-query-types';

import type { AnyApi } from '../buildCreateApi';
import type { GetStoreFn } from '../types';

import type { AnyEndpointQuery, AnyMutationDefinition, AnyQueryDefinition, UseQueryStateDefaultResult } from './types';

// Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
export type QueryEndpointCachedValues<Definition extends AnyQueryDefinition> = {
    /**
     * @soft-deprecated Метод скоро будет удален. Используйте endpoint.query()
     */
    getCachedValue: (
        queryArg: SkipToken | QueryArgFrom<Definition>
    ) => UseQueryStateResult<Definition, UseQueryStateDefaultResult<Definition>>;
    /**
     * @soft-deprecated Метод скоро будет удален. Используйте endpoint.query()
     */
    useCachedValues: (
        ...queryArgs: Array<SkipToken | QueryArgFrom<Definition>>
    ) => UseQueryStateResult<Definition, UseQueryStateDefaultResult<Definition>>[];
};

/** Тип параметров селектора результата мутациии */
type MutationResultSelectorParams = SkipToken | string | { requestId?: string; fixedCacheKey?: string };

/** Тип хука геттера `getCachedValue` и хука `useCachedValues` для mutation-эндпоинта */
export type MutationEndpointCachedValues<Definition extends AnyMutationDefinition> = {
    /**
     * @soft-deprecated Метод скоро будет удален. Используйте endpoint.query()
     */
    getCachedValue: <Result extends Record<string, unknown> = MutationResultSelectorResult<Definition>>(
        queryArg: MutationResultSelectorParams
    ) => UseMutationStateResult<Definition, Result>;
    /**
     * @soft-deprecated Метод скоро будет удален. Используйте endpoint.query()
     */
    useCachedValues: <Result extends Record<string, unknown> = MutationResultSelectorResult<Definition>>(
        ...queryArgs: MutationResultSelectorParams[]
    ) => UseMutationStateResult<Definition, Result>[];
};

type BuildOptions = {
    api: AnyApi;
    endpointName: string;
    getStore: GetStoreFn;
};

/**
 * Создает функцию для получения закешированного результата запроса.
 *
 * @param options – Параметры запроса
 * @returns Функция для получения закешированного результата запроса, которую можно вызывать без передачи
 * текущего стейта.
 */
export function buildEndpointQueryGetCachedValue(
    options: BuildOptions
): QueryEndpointCachedValues<AnyQueryDefinition>['getCachedValue'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = options.api.endpoints[options.endpointName] as AnyEndpointQuery;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Уточняем типы для конкретного эндпоинта
    return buildCachedValueGetter(
        options.getStore,
        endpoint
    ) as QueryEndpointCachedValues<AnyQueryDefinition>['getCachedValue'];
}

/**
 * Создает хук для получения закешированных результатов запроса по переданным параметрам запроса.
 *
 * @param options – Параметры запроса
 * @returns Хук для получения закешированных результатов запроса по переданным параметрам запроса.
 */
export function buildEndpointQueryUseCachedValues(
    options: BuildOptions
): QueryEndpointCachedValues<AnyQueryDefinition>['useCachedValues'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = options.api.endpoints[options.endpointName] as AnyEndpointQuery;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Уточняем типы для конкретного эндпоинта
    return buildUseCachedValuesHook(
        options.getStore,
        endpoint
    ) as QueryEndpointCachedValues<AnyQueryDefinition>['useCachedValues'];
}

/**
 * Создает функцию для получения закешированного результата мутации.
 *
 * @param options – Параметры мутации
 * @returns Функция для получения закешированного результата мутации, которую можно вызывать без передачи
 * текущего стейта.
 */
export function buildEndpointMutationGetCachedValue(
    options: BuildOptions
): MutationEndpointCachedValues<AnyMutationDefinition>['getCachedValue'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = options.api.endpoints[options.endpointName] as AnyEndpointQuery;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Уточняем типы для конкретного эндпоинта
    return buildCachedValueGetter(
        options.getStore,
        endpoint
    ) as MutationEndpointCachedValues<AnyMutationDefinition>['getCachedValue'];
}

/**
 * Создает хук для получения закешированных результатов запроса по переданным параметрам мутации.
 *
 * @param options – Параметры мутации
 * @returns Хук для получения закешированных результатов запроса по переданным параметрам запроса.
 */
export function buildEndpointMutationUseCachedValues(
    options: BuildOptions
): MutationEndpointCachedValues<AnyMutationDefinition>['useCachedValues'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = options.api.endpoints[options.endpointName] as AnyEndpointQuery;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Уточняем типы для конкретного эндпоинта
    return buildUseCachedValuesHook(
        options.getStore,
        endpoint
    ) as MutationEndpointCachedValues<AnyMutationDefinition>['useCachedValues'];
}

/**
 * Создает функцию для получения закешированного результата запроса.
 *
 * @param getStore – Функция, возвращающая стор, к которому будет привязан метод.
 * @param select – Метод, который будет привязан к стору.
 * @returns Функция для получения закешированного результата запроса, которую можно вызывать без передачи
 * текущего стейта.
 */
function buildCachedValueGetter(getStore: GetStoreFn, endpoint: AnyEndpointQuery) {
    return (queryArg: unknown) => endpoint.select(queryArg)(getStore().getState());
}

/**
 * Создает хук для получения закешированных результатов запроса по переданным параметрам запроса.
 *
 * @param useSelector – Хук `useSelector` для получения данных из стора
 * @param select – Метод, получения текущего состояния эндпоинта.
 * @returns Хук для получения закешированных результатов запроса по переданным параметрам запроса.
 */
function buildUseCachedValuesHook(getStore: GetStoreFn, endpoint: AnyEndpointQuery) {
    return function useCachedValues(...queryArgs: unknown[]) {
        const store = getStore();

        return useSyncExternalStoreWithSelector(
            store.subscribeBatched,
            store.getState,
            store.getState,
            state => queryArgs.map(queryArg => endpoint.select(queryArg)(state)),
            (previousCachedValues, nextCachedValues) =>
                Boolean(
                    previousCachedValues &&
                        nextCachedValues &&
                        previousCachedValues.length === nextCachedValues.length &&
                        previousCachedValues.every((previousCachedValue, index) =>
                            shallowEqual(previousCachedValue, nextCachedValues[index])
                        )
                )
        );
    };
}
